/**
 * The GrimoireCodexDB record model, shared by the API and the web app.
 *
 * Every record is edited as a whole *document*: an edit carries the document as it was
 * (`before`) and as the contributor wants it (`after`), and applying it writes the changed
 * fields back. The schemas below are the single definition of what a valid document is, so the
 * server validates submissions with them and the web app builds its forms from the same
 * vocabulary.
 *
 * Fields mirror Grimoire's `GameSystem` and `Book` models (grimoire/backend/models/library.py)
 * so a Grimoire library can round-trip through the database without losing anything. Two deliberate
 * differences: a book may belong to several systems, and publishers and creators are records
 * of their own rather than free-text strings.
 *
 * The catalogue covers tabletop RPGs (for Grimoire) and wargames (for Codex, the wargaming
 * manager). A system's `game_type` says which; a wargame system lists its factions. Rules content
 * (stat lines, points, rules text) is deliberately out of scope: it is the publisher's, and it
 * changes with every balance update. Maps, tokens and miniatures are out of scope too: even a
 * preview image of one is most of what makes it worth having.
 */
import { z } from 'zod'

export const ENTITY_TYPES = ['system', 'book', 'series', 'publisher', 'creator'] as const
export type EntityType = (typeof ENTITY_TYPES)[number]

/** URL path segment for each entity type (`/systems/:id`), and its database table. */
export const ENTITY_PATHS: Record<EntityType, string> = {
  system: 'systems',
  book: 'books',
  series: 'series',
  publisher: 'publishers',
  creator: 'creators',
}

/** What kind of game a system is. Books and series take it from their systems. */
export const GAME_TYPES = ['ttrpg', 'wargame'] as const
export type GameType = (typeof GAME_TYPES)[number]

// --- Vocabularies -----------------------------------------------------------------------------

/**
 * Suggested book categories. Grimoire derives categories from folder names, so the set is open:
 * any lowercase slug is valid and these are only the suggestions offered first.
 */
export const BOOK_CATEGORIES = [
  'core',
  'supplement',
  'adventure',
  'setting',
  'bestiary',
  'starter-set',
  'quickstart',
  'srd',
  'gm-tools',
  'character-sheet',
  'handout',
  'random-roll-tables',
  'worldbuilding',
  'map',
  'magazine',
  'homebrew',
  // Wargames
  'army-book',
  'campaign',
  'mission-pack',
  'errata',
  'painting-guide',
  'lore',
  'other',
] as const

/** Book categories that only make sense for wargames; not suggested while wargames are off. */
export const WARGAME_BOOK_CATEGORIES: readonly string[] = ['army-book', 'mission-pack', 'painting-guide']

/** Grimoire's variant kinds for books (backend/models/variants.py, VARIANT_KINDS_BY_TYPE). */
export const VARIANT_KINDS = [
  'version',
  'printer-friendly',
  'form-fillable',
  'spreads',
  'single-page',
  'black-and-white',
  'other',
] as const

/**
 * Grimoire's system container kinds (backend/indexer/constants.py). "" is an ordinary system;
 * "parent" holds editions, "agnostic" and "one-page" are the special collections, "family" and
 * "publisher" group related systems, "generic" groups without claiming a relationship.
 */
export const CONTAINER_KINDS = ['', 'parent', 'agnostic', 'one-page', 'family', 'publisher', 'generic'] as const

/** Roles a creator can have on a book. Grimoire stores authors and artists; the database adds the rest. */
export const CREDIT_ROLES = ['author', 'artist', 'editor', 'designer', 'cartographer', 'translator', 'layout'] as const
export type CreditRole = (typeof CREDIT_ROLES)[number]

export const FINGERPRINT_ALGOS = ['sha256', 'sha1', 'md5'] as const

/** Where a system is in its life. "" is unknown. */
export const SYSTEM_STATUSES = ['', 'active', 'in-development', 'playtest', 'discontinued', 'legacy'] as const
export const COMPLEXITIES = ['', 'rules-light', 'rules-medium', 'rules-heavy'] as const
export const CHARACTER_STYLES = [
  'class-based',
  'skill-based',
  'level-based',
  'classless',
  'point-buy',
  'playbook-based',
] as const
export const PLAY_MODES = ['gm-required', 'gm-optional', 'gm-less', 'solo', 'co-op'] as const
export const SYSTEM_RELATIONS = [
  'predecessor',
  'successor',
  'derived-from',
  'inspired-by',
  'compatible-with',
  'related',
] as const

/** How big a wargame is played: a handful of models up to whole armies. */
export const GAME_SIZES = ['skirmish', 'squad', 'battle', 'mass-battle'] as const

/** How a book is (or was) published. */
export const BOOK_FORMATS = [
  'pdf',
  'epub',
  'hardcover',
  'softcover',
  'boxed-set',
  'zine',
  'print-on-demand',
  'vtt-module',
  'zip',
] as const
export const AVAILABILITY = ['', 'free', 'pay-what-you-want', 'paid', 'out-of-print', 'unavailable'] as const
export const BOOK_RELATIONS = [
  'requires',
  'expands',
  'compatible-with',
  'revision-of',
  'remaster-of',
  'translation-of',
  'collects',
  'related',
] as const

/** Settings, series, product lines and collections a book can belong to. */
export const SERIES_KINDS = ['setting', 'series', 'product-line', 'collection', 'campaign'] as const

/** What a report about a record or an edit is for. */
export const REPORT_REASONS = ['incorrect', 'duplicate', 'spam', 'copyright', 'offensive', 'other'] as const
export type ReportReason = (typeof REPORT_REASONS)[number]

/** What a message sent from the support page is about. */
export const SUPPORT_CATEGORIES = ['bug', 'feature', 'feedback', 'account', 'privacy', 'other'] as const
export type SupportCategory = (typeof SUPPORT_CATEGORIES)[number]

// --- Field schemas ----------------------------------------------------------------------------

const text = (max: number) => z.string().trim().max(max).default('')
const list = <T extends z.ZodType>(item: T, max = 50) => z.array(item).max(max).default([])

const httpUrl = z
  .string()
  .trim()
  .max(1000)
  .refine((v) => /^https?:\/\/[^\s]+$/i.test(v), 'Must be an http(s) URL')

export const linkSchema = z.object({
  label: z.string().trim().max(80).default(''),
  url: httpUrl,
})
export type Link = z.infer<typeof linkSchema>

/**
 * A reference to another record. `id` points at an existing one; a reference with only a
 * `name` asks for a new publisher/creator to be created (or an exact-name match reused) when
 * the edit is applied, so a contributor never has to leave the book form to add an author.
 */
export const refSchema = z.object({
  id: z.string().trim().max(40).optional(),
  name: z.string().trim().min(1).max(255),
})
export type Ref = z.infer<typeof refSchema>

/** A reference that must point at an existing record (systems and books are never auto-created). */
export const idRefSchema = refSchema.extend({ id: z.string().trim().min(1).max(40) })
export type IdRef = z.infer<typeof idRefSchema>

/** Membership of a series, with the book's number in it ("3", "A1") when it has one. */
export const seriesRefSchema = refSchema.extend({ number: z.string().trim().max(20).default('') })
export type SeriesRef = z.infer<typeof seriesRefSchema>

/** An identifier in another catalogue: `{ source: "drivethrurpg", id: "521240" }`. */
export const externalIdSchema = z.object({
  source: z
    .string()
    .trim()
    .toLowerCase()
    .min(1)
    .max(40)
    .regex(/^[a-z0-9][a-z0-9.-]*$/, 'Use a short lowercase source name, e.g. drivethrurpg'),
  id: z.string().trim().min(1).max(200),
})
export type ExternalId = z.infer<typeof externalIdSchema>

export const creditSchema = z.object({
  role: z.enum(CREDIT_ROLES),
  creator: refSchema,
})
export type Credit = z.infer<typeof creditSchema>

export const fingerprintSchema = z.object({
  algo: z.enum(FINGERPRINT_ALGOS),
  hash: z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[0-9a-f]{32,128}$/, 'Must be a hex digest'),
  size: z.number().int().nonnegative().nullable().default(null),
})
export type Fingerprint = z.infer<typeof fingerprintSchema>

/**
 * Fields that may hold someone else's writing. Everything else in a document is a fact, which is
 * CC0 whoever entered it.
 */
export const ATTRIBUTABLE_FIELDS = ['description', 'tags'] as const
export type AttributableField = (typeof ATTRIBUTABLE_FIELDS)[number]

/**
 * Credit for fields that came from another catalogue and are *not* released under CC0: the
 * source's text stays on the record, but anyone reusing those fields has to credit the source.
 * The record's other fields are CC0 as usual.
 */
export const attributionSchema = z.object({
  fields: z.array(z.enum(ATTRIBUTABLE_FIELDS)).min(1).max(ATTRIBUTABLE_FIELDS.length),
  /** Who to credit, by name: "TTRPG Wiki". */
  source: z.string().trim().min(1).max(120),
  /** The record's page at the source, linked wherever the fields are shown. */
  url: httpUrl,
})
export type Attribution = z.infer<typeof attributionSchema>

const attributions = z
  .array(attributionSchema)
  .max(5)
  .default([])
  .transform((v) => v.map((a) => ({ ...a, fields: [...new Set(a.fields)] })))
  .describe(
    'Fields of this record taken from another catalogue. They are NOT CC0: anyone reusing them must credit ' +
      '`source` and link `url`. Every other field is CC0.',
  )

/** The fields of a document that are not CC0, with who to credit for each. */
export function attributedFields(doc: { attributions?: Attribution[] }): Map<AttributableField, Attribution> {
  const out = new Map<AttributableField, Attribution>()
  for (const a of doc.attributions ?? []) for (const f of a.fields) if (!out.has(f)) out.set(f, a)
  return out
}

const year = z.number().int().min(1950).max(2100).nullable().default(null)
const count = (max: number, min = 0) => z.number().int().min(min).max(max).nullable().default(null)
const enumList = <T extends readonly [string, ...string[]]>(values: T) =>
  z
    .array(z.enum(values))
    .max(values.length)
    .default([])
    .transform((v) => [...new Set(v)])
const externalIds = z
  .array(externalIdSchema)
  .max(30)
  .default([])
  .refine((v) => new Set(v.map((e) => `${e.source}:${e.id}`)).size === v.length, 'An identifier is listed twice')
const tags = list(z.string().trim().min(1).max(80), 100)
const names = (max = 30) => list(z.string().trim().min(1).max(150), max)
const storageKey = z
  .string()
  .regex(/^covers\/[0-9a-f]{64}\.(webp|png|jpg|gif|avif)$/)
  .nullable()
  .default(null)

// --- Documents --------------------------------------------------------------------------------

export const systemDocSchema = z
  .object({
    name: z.string().trim().min(1).max(255),
    aliases: names(),
    game_type: z.enum(GAME_TYPES).default('ttrpg'),
    description: text(20000),
    edition: text(80),
    parent_system: text(150),
    system_family: text(150),
    genres: names(),
    dice_materials: names(),
    license: text(150),
    year,
    publishers: list(refSchema, 30),
    urls: list(linkSchema),
    character_builder_urls: list(linkSchema),
    tags,
    is_explicit: z.boolean().default(false),
    is_system_agnostic: z.boolean().default(false),
    is_one_page: z.boolean().default(false),
    container_kind: z.enum(CONTAINER_KINDS).default(''),
    parent_id: z.string().max(40).nullable().default(null),
    cover: storageKey,
    status: z.enum(SYSTEM_STATUSES).default(''),
    complexity: z.enum(COMPLEXITIES).default(''),
    mechanics: names(),
    character_styles: enumList(CHARACTER_STYLES),
    play_modes: enumList(PLAY_MODES),
    players_min: count(100, 1),
    players_max: count(100, 1),
    session_styles: names(10),
    // Wargames
    miniature_scales: names(10),
    game_sizes: enumList(GAME_SIZES),
    /** The armies a wargame is played with. Names only: no rules, stat lines or points. */
    factions: names(150),
    relations: z
      .array(z.object({ type: z.enum(SYSTEM_RELATIONS), system: idRefSchema }))
      .max(50)
      .default([]),
    external_ids: externalIds,
    attributions,
  })
  .refine((d) => d.players_min === null || d.players_max === null || d.players_min <= d.players_max, {
    message: 'The fewest players must not be more than the most',
    path: ['players_max'],
  })
export type SystemDoc = z.infer<typeof systemDocSchema>

export const bookDocSchema = z
  .object({
    title: z.string().trim().min(1).max(500),
    subtitle: text(500),
    description: text(20000),
    category: z
      .string()
      .trim()
      .toLowerCase()
      .max(100)
      .default('other')
      .transform((v) => slugify(v) || 'other'),
    systems: list(idRefSchema, 20),
    credits: list(creditSchema, 100),
    publishers: list(refSchema, 20),
    urls: list(linkSchema),
    genres: names(),
    isbn: z
      .string()
      .trim()
      .max(20)
      .default('')
      .transform((v) => v.replace(/[\s-]/g, '').toUpperCase())
      .refine((v) => v === '' || /^(\d{9}[\dX]|\d{13})$/.test(v), 'ISBN must be 10 or 13 digits'),
    product_code: text(100),
    version: text(50),
    language: text(20),
    license: text(150),
    year,
    month: z.number().int().min(1).max(12).nullable().default(null),
    day: z.number().int().min(1).max(31).nullable().default(null),
    page_count: z.number().int().min(0).max(100000).nullable().default(null),
    is_explicit: z.boolean().default(false),
    variant_of: z.string().max(40).nullable().default(null),
    variant_kind: z.union([z.enum(VARIANT_KINDS), z.literal('')]).default(''),
    variant_label: text(120),
    tags,
    cover: storageKey,
    /** No cover image, because the cover would give the product away (a one-page game, a map). */
    cover_withheld: z.boolean().default(false),
    fingerprints: list(fingerprintSchema, 50),
    formats: enumList(BOOK_FORMATS),
    availability: z.enum(AVAILABILITY).default(''),
    license_notes: text(1000),
    level_min: count(30),
    level_max: count(30),
    series: list(seriesRefSchema, 20),
    relations: z
      .array(z.object({ type: z.enum(BOOK_RELATIONS), book: idRefSchema }))
      .max(50)
      .default([]),
    external_ids: externalIds,
    attributions,
  })
  .refine((d) => d.level_min === null || d.level_max === null || d.level_min <= d.level_max, {
    message: 'The lowest level must not be above the highest',
    path: ['level_max'],
  })
  .refine((d) => d.day === null || d.month !== null, {
    message: 'A day needs a month',
    path: ['day'],
  })
  .refine((d) => d.month === null || d.year !== null, {
    message: 'A month needs a year',
    path: ['month'],
  })
  // A withheld cover is never stored, whichever way the document arrived (form, API, import).
  .overwrite((d) => (d.cover && coverWithheld(d) ? { ...d, cover: null } : d))
export type BookDoc = z.infer<typeof bookDocSchema>

/**
 * Whether a book must go without a cover. A one-page book's cover is the whole product, so it is
 * withheld automatically; anything else (a map pack, a two-page game) can be withheld by hand.
 */
export function coverWithheld(d: { cover_withheld?: boolean; page_count?: number | null }): boolean {
  return Boolean(d.cover_withheld) || d.page_count === 1
}

export const seriesDocSchema = z.object({
  name: z.string().trim().min(1).max(255),
  aliases: names(),
  kind: z.enum(SERIES_KINDS).default('series'),
  description: text(20000),
  systems: list(idRefSchema, 30),
  publishers: list(refSchema, 20),
  parent_id: z.string().max(40).nullable().default(null),
  urls: list(linkSchema),
  tags,
  cover: storageKey,
  is_explicit: z.boolean().default(false),
  external_ids: externalIds,
  attributions,
})
export type SeriesDoc = z.infer<typeof seriesDocSchema>

export const publisherDocSchema = z.object({
  name: z.string().trim().min(1).max(255),
  aliases: names(),
  description: text(20000),
  urls: list(linkSchema),
  parent_id: z.string().max(40).nullable().default(null),
  tags,
  attributions,
})
export type PublisherDoc = z.infer<typeof publisherDocSchema>

export const creatorDocSchema = z.object({
  name: z.string().trim().min(1).max(255),
  aliases: names(),
  description: text(20000),
  /** A portrait the person shares publicly, like an author photo on a book jacket. Optional. */
  photo: storageKey,
  urls: list(linkSchema),
  tags,
  attributions,
})
export type CreatorDoc = z.infer<typeof creatorDocSchema>

export type AnyDoc = SystemDoc | BookDoc | SeriesDoc | PublisherDoc | CreatorDoc
export interface DocByType {
  system: SystemDoc
  book: BookDoc
  series: SeriesDoc
  publisher: PublisherDoc
  creator: CreatorDoc
}

export const DOC_SCHEMAS = {
  system: systemDocSchema,
  book: bookDocSchema,
  series: seriesDocSchema,
  publisher: publisherDocSchema,
  creator: creatorDocSchema,
} as const

const BLANK_SEED: Record<EntityType, Record<string, unknown>> = {
  system: { name: '-' },
  book: { title: '-' },
  series: { name: '-' },
  publisher: { name: '-' },
  creator: { name: '-' },
}

/** An empty document of a type, every field at its default. The name/title is blank. */
export function blankDoc<T extends EntityType>(type: T): DocByType[T] {
  const doc = DOC_SCHEMAS[type].parse(BLANK_SEED[type]) as Record<string, unknown>
  return { ...doc, ...('title' in doc ? { title: '' } : { name: '' }) } as unknown as DocByType[T]
}

/**
 * A stored document brought up to the current schema: fields added since it was written get
 * their defaults. Documents that no longer validate (data written under older rules) keep their
 * values over the defaults rather than being rejected on read.
 */
export function normalizeDoc<T extends EntityType>(type: T, raw: unknown): DocByType[T] {
  const parsed = DOC_SCHEMAS[type].safeParse(raw)
  if (parsed.success) return parsed.data as DocByType[T]
  return { ...blankDoc(type), ...(raw as object) } as DocByType[T]
}

/** The display name of a document, whatever its type. */
export function docTitle(type: EntityType, doc: AnyDoc): string {
  return type === 'book' ? (doc as BookDoc).title : (doc as SystemDoc).name
}

// --- References between records ---------------------------------------------------------------

/**
 * Every field that points at another record, per type. This one table drives reference
 * resolution (name-only publishers/creators/series are created on apply), the `links` projection
 * used for reverse lookups, merges (repointing references), ownership, and display.
 *
 * Shapes: `list` is `Ref[]`; `credit` is `{ role, creator: Ref }[]`; `relation` is
 * `{ type, <item>: IdRef }[]`.
 */
export interface RefSpec {
  field: string
  target: EntityType
  rel: string
  shape: 'list' | 'credit' | 'relation'
  /** For relations: the key holding the reference. */
  item?: string
  /** Name-only references create a record of `target` on apply. */
  create?: boolean
}

export const REF_FIELDS: Record<EntityType, RefSpec[]> = {
  system: [
    { field: 'publishers', target: 'publisher', rel: 'publisher', shape: 'list', create: true },
    { field: 'relations', target: 'system', rel: 'relation', shape: 'relation', item: 'system' },
  ],
  book: [
    { field: 'systems', target: 'system', rel: 'system', shape: 'list' },
    { field: 'credits', target: 'creator', rel: 'credit', shape: 'credit', create: true },
    { field: 'publishers', target: 'publisher', rel: 'publisher', shape: 'list', create: true },
    { field: 'series', target: 'series', rel: 'series', shape: 'list', create: true },
    { field: 'relations', target: 'book', rel: 'relation', shape: 'relation', item: 'book' },
  ],
  series: [
    { field: 'systems', target: 'system', rel: 'system', shape: 'list' },
    { field: 'publishers', target: 'publisher', rel: 'publisher', shape: 'list', create: true },
  ],
  publisher: [],
  creator: [],
}

/** Single-id fields (`parent_id`, `variant_of`) and the type they point at. */
export const PARENT_FIELDS: Record<EntityType, Array<{ field: string; target: EntityType }>> = {
  system: [{ field: 'parent_id', target: 'system' }],
  book: [{ field: 'variant_of', target: 'book' }],
  series: [{ field: 'parent_id', target: 'series' }],
  publisher: [{ field: 'parent_id', target: 'publisher' }],
  creator: [],
}

export interface RefVisit {
  spec: RefSpec
  ref: Ref
  /** Credit role or relation type, when the shape has one. */
  role: string
  position: number
}

/** Every reference held by a document. */
export function refsOf(type: EntityType, doc: AnyDoc): RefVisit[] {
  const out: RefVisit[] = []
  const d = doc as unknown as Record<string, unknown>
  for (const spec of REF_FIELDS[type]) {
    const items = (d[spec.field] as unknown[] | undefined) ?? []
    items.forEach((item, position) => {
      const o = item as Record<string, unknown>
      if (spec.shape === 'list') out.push({ spec, ref: o as unknown as Ref, role: '', position })
      else if (spec.shape === 'credit') out.push({ spec, ref: o.creator as Ref, role: String(o.role), position })
      else out.push({ spec, ref: o[spec.item!] as Ref, role: String(o.type), position })
    })
  }
  return out
}

/** A copy of a document with every reference passed through `fn`. */
export function mapRefs<T extends AnyDoc>(type: EntityType, doc: T, fn: (ref: Ref, spec: RefSpec) => Ref): T {
  const d = { ...(doc as unknown as Record<string, unknown>) }
  for (const spec of REF_FIELDS[type]) {
    const items = (d[spec.field] as Array<Record<string, unknown>> | undefined) ?? []
    d[spec.field] = items.map((item) => {
      if (spec.shape === 'list') return { ...item, ...fn(item as unknown as Ref, spec) }
      if (spec.shape === 'credit') return { ...item, creator: fn(item.creator as Ref, spec) }
      return { ...item, [spec.item!]: fn(item[spec.item!] as Ref, spec) }
    })
  }
  return d as unknown as T
}

/**
 * Fields a contributor below editor may not change without review, however trusted: renaming
 * or moving a record, or flagging it explicit, changes what everyone else sees and searches.
 * Credits are reviewed too: dropping one would quietly put someone else's writing under CC0,
 * and adding one puts an outside link on the record.
 */
export const HIGH_RISK_FIELDS: Record<EntityType, string[]> = {
  system: ['name', 'game_type', 'parent_id', 'container_kind', 'is_explicit', 'attributions'],
  book: ['title', 'systems', 'variant_of', 'is_explicit', 'cover_withheld', 'attributions'],
  series: ['name', 'parent_id', 'is_explicit', 'attributions'],
  publisher: ['name', 'parent_id', 'attributions'],
  creator: ['name', 'attributions'],
}

/** Lowercase, hyphenated, ASCII-only slug ("Random Roll Tables" -> "random-roll-tables"). */
export function slugify(v: string): string {
  return v
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 100)
}

// --- Field labels -----------------------------------------------------------------------------

/** Human labels for every document field, used by forms, diffs and the gap list. */
export const FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  title: 'Title',
  aliases: 'Also known as',
  description: 'Description',
  edition: 'Edition',
  parent_system: 'Parent system',
  system_family: 'System family',
  genres: 'Genres',
  dice_materials: 'Dice & materials',
  license: 'License',
  year: 'Year',
  month: 'Month',
  day: 'Day',
  publishers: 'Publishers',
  urls: 'Links',
  character_builder_urls: 'Character builders',
  tags: 'Tags',
  is_explicit: 'Explicit content',
  is_system_agnostic: 'System-agnostic',
  is_one_page: 'One-page RPG',
  container_kind: 'Container',
  parent_id: 'Part of',
  cover: 'Cover',
  category: 'Category',
  systems: 'Systems',
  credits: 'Credits',
  isbn: 'ISBN',
  product_code: 'Product code',
  version: 'Version',
  language: 'Language',
  page_count: 'Pages',
  variant_of: 'Variant of',
  variant_kind: 'Variant kind',
  variant_label: 'Variant label',
  fingerprints: 'File fingerprints',
  status: 'Status',
  complexity: 'Rules weight',
  mechanics: 'Core mechanics',
  character_styles: 'Character building',
  play_modes: 'Ways to play',
  players_min: 'Fewest players',
  players_max: 'Most players',
  session_styles: 'Session styles',
  relations: 'Related',
  external_ids: 'External IDs',
  subtitle: 'Subtitle',
  formats: 'Formats',
  availability: 'Availability',
  license_notes: 'License notes',
  level_min: 'Lowest level',
  level_max: 'Highest level',
  series: 'Series',
  kind: 'Kind',
  game_type: 'Game type',
  miniature_scales: 'Miniature scales',
  game_sizes: 'Game sizes',
  factions: 'Factions',
  photo: 'Portrait',
  cover_withheld: 'Cover withheld',
  attributions: 'Credited sources',
}

// --- Completeness -----------------------------------------------------------------------------

/**
 * The fields a complete record should have, with weights. Drives the gold gauge on every cover
 * and the "missing" list on a record page. Optional-by-nature fields (edition, variant label,
 * aliases) are deliberately absent: an empty value there is usually correct, and counting it
 * would nag contributors into inventing data.
 */
const COMPLETENESS: Record<EntityType, Array<[field: string, weight: number]>> = {
  system: [
    ['description', 3],
    ['cover', 2],
    ['publishers', 2],
    ['genres', 2],
    ['year', 1],
    ['license', 1],
    ['system_family', 1],
    ['dice_materials', 1],
    ['urls', 1],
    ['tags', 1],
    ['play_modes', 1],
    ['players', 1],
  ],
  book: [
    ['description', 3],
    ['cover', 2],
    ['systems', 2],
    ['credits', 2],
    ['publishers', 2],
    ['year', 1],
    ['page_count', 1],
    ['identifier', 1],
    ['language', 1],
    ['urls', 1],
  ],
  series: [
    ['description', 2],
    ['systems', 1],
    ['publishers', 1],
    ['cover', 1],
    ['urls', 1],
  ],
  publisher: [
    ['description', 2],
    ['urls', 2],
  ],
  creator: [
    ['description', 1],
    ['urls', 1],
  ],
}

/** A one-page RPG. The flag means nothing on a wargame (the form hides it there). */
export function isOnePageRpg(d: { is_one_page?: boolean; game_type?: GameType }): boolean {
  return Boolean(d.is_one_page) && d.game_type !== 'wargame'
}

/** Synthetic gap-list fields that map to more than one document field. */
export const GAP_LABELS: Record<string, string> = {
  ...FIELD_LABELS,
  identifier: 'ISBN or product code',
  players: 'Player count',
}

function isFilled(doc: Record<string, unknown>, field: string): boolean {
  if (field === 'identifier') return Boolean(doc.isbn || doc.product_code)
  if (field === 'players') return doc.players_min != null || doc.players_max != null
  const v = doc[field]
  if (Array.isArray(v)) return v.length > 0
  if (typeof v === 'string') return v.trim() !== ''
  return v !== null && v !== undefined
}

export interface Completeness {
  /** 0-100, weighted. */
  score: number
  /** Gap-tracked fields that are empty, heaviest first. */
  missing: string[]
}

/**
 * Wargame systems swap the TTRPG-only gaps (ways to play, which assume a GM or not) for the
 * questions a wargamer asks first: which armies there are, what scale the models are and how big
 * a game is.
 */
const WARGAME_SYSTEM: Array<[field: string, weight: number]> = [
  ...COMPLETENESS.system.filter(([f]) => f !== 'play_modes'),
  ['miniature_scales', 1],
  ['game_sizes', 1],
  ['factions', 2],
]

export function completeness(type: EntityType, doc: AnyDoc): Completeness {
  const rules = type === 'system' && (doc as SystemDoc).game_type === 'wargame' ? WARGAME_SYSTEM : COMPLETENESS[type]
  const record = doc as unknown as Record<string, unknown>
  let total = 0
  let have = 0
  const missing: Array<[string, number]> = []
  for (const [field, weight] of rules) {
    // A withheld cover is not a gap: there is deliberately no image to add. Nor is a one-page
    // RPG's: most have no cover but the game itself, so its title card stands in.
    if (field === 'cover' && type === 'book' && coverWithheld(doc as BookDoc)) continue
    if (field === 'cover' && type === 'system' && isOnePageRpg(doc as SystemDoc)) continue
    total += weight
    if (isFilled(record, field)) have += weight
    else missing.push([field, weight])
  }
  missing.sort((a, b) => b[1] - a[1])
  return {
    score: total ? Math.round((have / total) * 100) : 100,
    missing: missing.map(([f]) => f),
  }
}

// --- Users and edits --------------------------------------------------------------------------

export const ROLES = ['user', 'editor', 'moderator', 'admin'] as const
export type Role = (typeof ROLES)[number]
const RANK: Record<Role, number> = { user: 0, editor: 1, moderator: 2, admin: 3 }
export const atLeast = (role: Role | undefined | null, min: Role) => role != null && RANK[role] >= RANK[min]

export const EDIT_ACTIONS = ['create', 'update', 'delete', 'merge'] as const
export type EditAction = (typeof EDIT_ACTIONS)[number]
export const EDIT_STATUSES = ['draft', 'pending', 'applied', 'rejected', 'withdrawn'] as const
export type EditStatus = (typeof EDIT_STATUSES)[number]

export const AUTH_PROVIDERS = ['discord', 'github', 'google'] as const
export type AuthProvider = (typeof AUTH_PROVIDERS)[number]

/** The changed top-level fields between two documents. */
export function changedFields(before: object | null, after: object | null): string[] {
  const a = (before ?? {}) as Record<string, unknown>
  const b = (after ?? {}) as Record<string, unknown>
  const keys = new Set([...Object.keys(a), ...Object.keys(b)])
  return [...keys].filter((k) => !sameValue(a[k], b[k])).sort()
}

/**
 * An edit's changes laid onto `base`: `base` with the fields the author changed set to their
 * values. What a reviewer correcting an edit starts from, and what their corrections are measured
 * against, so changes made to the record since the edit was submitted are not counted as theirs.
 */
export function rebaseEdit<T extends object>(base: T, after: object, fields: string[]): T {
  const a = after as Record<string, unknown>
  return { ...base, ...Object.fromEntries(fields.map((f) => [f, a[f]])) }
}

/**
 * Structural equality for JSON values (key order independent). References compare by id: a
 * `{id, name}` ref whose target was renamed since is still the same value, otherwise a rename
 * would make every edit touching that list look like a change (or a conflict).
 */
export function sameValue(a: unknown, b: unknown): boolean {
  return stableJson(canonRefs(a ?? null)) === stableJson(canonRefs(b ?? null))
}

function canonRefs(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(canonRefs)
  if (v && typeof v === 'object') {
    const o = v as Record<string, unknown>
    const keys = Object.keys(o).filter((k) => o[k] !== undefined)
    const named = typeof o.id === 'string' && o.id !== '' && 'name' in o
    return Object.fromEntries(keys.filter((k) => !(named && k === 'name')).map((k) => [k, canonRefs(o[k])]))
  }
  return v
}

export function stableJson(v: unknown): string {
  if (Array.isArray(v)) return `[${v.map(stableJson).join(',')}]`
  if (v && typeof v === 'object') {
    const o = v as Record<string, unknown>
    return `{${Object.keys(o)
      .filter((k) => o[k] !== undefined)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${stableJson(o[k])}`)
      .join(',')}}`
  }
  return JSON.stringify(v)
}
